/**
 * As peças do caso numa tabela navegável (E78.F3.T07).
 *
 * Substitui os N cards empilhados — no caso real, cinco blocos com o formulário
 * inteiro e o quadro completo dentro de cada um. Aqui as cinco cabem numa tela:
 * letra e arquivo, o código declarado no protocolo (editável, porque é o único
 * dado que muda de peça para peça), o código apurado sobre o arquivo recebido,
 * o confronto entre os dois, grau, alertas e situação. Clicar na linha abre o
 * quadro daquela peça.
 *
 * O confronto e a contagem de alertas são LIDOS do quadro que o motor gravou —
 * a tabela não compara hash nenhum.
 */
import {
  Badge,
  Button,
  Input,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type BadgeProps,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  ExameEletronicoCcbData,
  ExameEletronicoStatus,
  SituacaoItem,
} from '@/lib/exame_eletronico';
import { alertasDaPeca, confrontoDaPeca, tituloDaPeca } from '@/lib/exame_eletronico_peca';

const t = ptBR.exameEletronico;
const tp = ptBR.exameEletronico.tabelaDePecas;
const tq = ptBR.exameEletronico.quadro;

const STATUS_META: Record<
  ExameEletronicoStatus | 'sem_protocolo',
  { rotulo: string; variant: NonNullable<BadgeProps['variant']> }
> = {
  sem_protocolo: { rotulo: t.statusSemProtocolo, variant: 'neutral' },
  rascunho: { rotulo: t.statusRascunho, variant: 'info' },
  verificado: { rotulo: t.statusVerificado, variant: 'success' },
  concluido: { rotulo: t.statusConcluido, variant: 'success' },
};

const CONFRONTO_META: Record<
  SituacaoItem,
  { rotulo: string; variant: NonNullable<BadgeProps['variant']> }
> = {
  confere: { rotulo: tp.confrontoConfere, variant: 'success' },
  nao_confere: { rotulo: tp.confrontoNaoConfere, variant: 'danger' },
  ausente: { rotulo: tq.situacao.ausente, variant: 'warning' },
  nao_executado: { rotulo: tq.situacao.nao_executado, variant: 'neutral' },
};

/** Os 64 caracteres do código não cabem na coluna; o meio some, as pontas ficam. */
function abreviado(valor: string): string {
  return valor.length <= 20 ? valor : `${valor.slice(0, 8)}…${valor.slice(-6)}`;
}

export interface TabelaDePecasProps {
  pecas: readonly ExameEletronicoCcbData[];
  /** `source_document_id` da peça cujo quadro está aberto. */
  selecionada: string | null;
  /** Código declarado em edição, por peça (o salvo, enquanto ninguém digita). */
  rascunhos: Readonly<Record<string, string>>;
  /** Peça com gravação em voo — trava a linha dela. */
  salvando: string | null;
  erroPorPeca: Readonly<Record<string, string>>;
  onSelecionar: (sourceDocumentId: string) => void;
  onEditarCodigo: (sourceDocumentId: string, codigo: string) => void;
  onSalvarPeca: (sourceDocumentId: string) => void;
}

function CelulaCodigoDeclarado({
  peca,
  valor,
  salvando,
  erro,
  onEditar,
  onSalvar,
}: {
  peca: ExameEletronicoCcbData;
  valor: string;
  salvando: boolean;
  erro: string | undefined;
  onEditar: (codigo: string) => void;
  onSalvar: () => void;
}): JSX.Element {
  const concluido = peca.exame?.status === 'concluido';
  const alterado = valor !== (peca.codigo_declarado ?? '');
  return (
    // Editar não é navegar: o clique dentro da célula não troca a peça aberta.
    <div
      className="space-y-sc-1"
      onClick={(evento) => evento.stopPropagation()}
      role="presentation"
    >
      <Input
        aria-label={`${tp.colunaDeclarado} — ${tituloDaPeca(peca, 0)}`}
        className="w-full min-w-[12rem] font-mono text-body-sm"
        value={valor}
        disabled={salvando || concluido}
        maxLength={128}
        onChange={(evento) => onEditar(evento.target.value)}
        data-testid={`peca-codigo-${peca.source_document_id}`}
      />
      {erro !== undefined ? (
        <p
          className="text-caption text-danger-700"
          role="alert"
          data-testid={`peca-erro-${peca.source_document_id}`}
        >
          {erro}
        </p>
      ) : null}
      {alterado && !concluido ? (
        <Button
          type="button"
          variant="secondary"
          disabled={salvando}
          onClick={onSalvar}
          data-testid={`peca-salvar-${peca.source_document_id}`}
        >
          {salvando ? t.form.salvando : tp.salvarCodigo}
        </Button>
      ) : null}
    </div>
  );
}

export function TabelaDePecas({
  pecas,
  selecionada,
  rascunhos,
  salvando,
  erroPorPeca,
  onSelecionar,
  onEditarCodigo,
  onSalvarPeca,
}: TabelaDePecasProps): JSX.Element {
  return (
    <div className="overflow-x-auto" data-testid="tabela-de-pecas">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{tp.colunaPeca}</TableHead>
            <TableHead>{tp.colunaDeclarado}</TableHead>
            <TableHead>{tp.colunaApurado}</TableHead>
            <TableHead>{tp.colunaConfronto}</TableHead>
            <TableHead>{tp.colunaGrau}</TableHead>
            <TableHead>{tp.colunaAlertas}</TableHead>
            <TableHead>{tp.colunaSituacao}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pecas.map((peca, indice) => {
            const docId = peca.source_document_id;
            const titulo = tituloDaPeca(peca, indice);
            const status = peca.exame?.status ?? 'sem_protocolo';
            const confronto = confrontoDaPeca(peca);
            const alertas = alertasDaPeca(peca);
            const grau = peca.exame?.resultado?.grau_convergencia ?? null;
            return (
              <TableRow
                key={docId}
                className="cursor-pointer"
                data-testid="peca-linha"
                data-source-document={docId}
                data-selecionada={selecionada === docId}
                onClick={() => onSelecionar(docId)}
              >
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    className="px-0 text-left"
                    onClick={() => onSelecionar(docId)}
                    data-testid={`peca-abrir-${docId}`}
                    aria-label={tp.abrirQuadro.replace('{peca}', titulo)}
                  >
                    {titulo}
                  </Button>
                </TableCell>
                <TableCell>
                  <CelulaCodigoDeclarado
                    peca={peca}
                    valor={rascunhos[docId] ?? ''}
                    salvando={salvando === docId}
                    erro={erroPorPeca[docId]}
                    onEditar={(codigo) => onEditarCodigo(docId, codigo)}
                    onSalvar={() => onSalvarPeca(docId)}
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {peca.documento_content_sha256 === null ? (
                    <span className="text-body-sm text-fg-muted">{tp.apuradoPendente}</span>
                  ) : (
                    <MonoData title={peca.documento_content_sha256}>
                      {abreviado(peca.documento_content_sha256)}
                    </MonoData>
                  )}
                </TableCell>
                <TableCell>
                  {confronto === null ? (
                    <span className="text-body-sm text-fg-muted">{tp.confrontoPendente}</span>
                  ) : (
                    <Badge
                      variant={CONFRONTO_META[confronto].variant}
                      data-testid={`peca-confronto-${docId}`}
                      data-situacao={confronto}
                    >
                      {CONFRONTO_META[confronto].rotulo}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {grau === null ? '—' : tq.grauOpcoes[grau]}
                </TableCell>
                <TableCell data-testid={`peca-alertas-${docId}`}>{alertas ?? '—'}</TableCell>
                <TableCell>
                  <Badge
                    variant={STATUS_META[status].variant}
                    data-testid={`peca-situacao-${docId}`}
                    data-status={status}
                  >
                    {STATUS_META[status].rotulo}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
